import { QualityScore } from '../Quality';
import { PreflightTestReport } from 'twilio-video';
import { MediaConnectionBitrateTest } from '@twilio/rtc-diagnostics';

export function getSingleQualityScore(
  stat: number | undefined,
  goodThreshold: number,
  suboptimalThreshold: number,
  poorThreshold: number,
  descending: boolean = false
): QualityScore | null {
  if (typeof stat === 'undefined') {
    // Missing data should not produce a score — return null so callers
    // can distinguish "no data" from an actual quality assessment.
    return null;
  }

  if (descending) {
    if (stat > goodThreshold) return QualityScore.Excellent;
    if (stat > suboptimalThreshold) return QualityScore.Good;
    if (stat > poorThreshold) return QualityScore.Suboptimal;
    return QualityScore.Poor;
  }

  if (stat >= poorThreshold) return QualityScore.Poor;
  if (stat >= suboptimalThreshold) return QualityScore.Suboptimal;
  if (stat >= goodThreshold) return QualityScore.Good;
  return QualityScore.Excellent;
}

export const formatNumber = (val: number | undefined) => {
  return val?.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Calculate the delay impairment factor (Idd) from one-way delay.
 * Based on ITU-T G.107 Section 7.6.
 *
 * Uses the standard simplified formula:
 *   Idd = 0.024 * d + 0.11 * (d - 177.3) * H(d - 177.3)
 *
 * Where H(x) is the Heaviside step function: H(x) = 0 for x < 0, 1 for x >= 0.
 * Below 177.3ms, delay impairment grows linearly at 0.024 per ms.
 * Above 177.3ms, it grows more steeply (0.134 per ms) as interactivity
 * degrades significantly.
 *
 * @param d - Absolute one-way mouth-to-ear delay in milliseconds
 */
export function calculateIdd(d: number): number {
  if (d <= 0) return 0;
  const h = d > 177.3 ? 1 : 0;
  return 0.024 * d + 0.11 * (d - 177.3) * h;
}

/**
 * Calculate the effective equipment impairment factor (Ie-eff) from packet loss.
 * Based on ITU-T G.107 Section 7.8 and G.113 Appendix I.
 *
 * Formula: Ie_eff = Ie + (95 - Ie) * Ppl / (Ppl + Bpl)
 *
 * For WebRTC (Opus codec with PLC):
 *   Ie = 0  (negligible codec impairment for wideband Opus)
 *   Bpl = 4.3 (conservative robustness, similar to G.711 without PLC)
 *
 * @param packetLossPercent - Packet loss as a percentage (0-100)
 */
export function calculateIeEff(packetLossPercent: number): number {
  const ie = 0;    // Opus/WebRTC codec impairment at zero loss
  const bpl = 4.3; // Packet-loss robustness factor (G.113, G.711 baseline)
  return ie + (95 - ie) * packetLossPercent / (packetLossPercent + bpl);
}

/**
 * Convert R-factor to MOS using the standard ITU-T G.107 Annex B formula.
 *
 * MOS = 1 + 0.035*R + R*(R - 60)*(100 - R)*7e-6
 *
 * Constrained to: R < 0 → MOS = 1, R > 100 → MOS = 4.5
 *
 * @param r - R-factor (0-100)
 */
function rFactorToMOS(r: number): number {
  if (r < 0) return 1;
  if (r > 100) return 4.5;
  return 1 + 0.035 * r + r * (r - 60) * (100 - r) * 7e-6;
}

/**
 * Calculate MOS (Mean Opinion Score) using the ITU-T G.107 E-model.
 *
 * Implements the simplified E-model: R = R0 - Id - Ie_eff
 * where R0 = 93.2 (default narrowband baseline with Is and A at defaults).
 *
 * The effective one-way delay (Ta) is estimated as:
 *   Ta = (RTT / 2) + 2 * jitter
 * This accounts for the jitter buffer adding delay to smooth packet arrival.
 *
 * @param latency  - Round-trip time (RTT) in milliseconds
 * @param jitter   - Jitter in milliseconds (NOTE: Twilio SDK returns jitter in seconds
 *                    per WebRTC RFC 3550 — callers must convert with `* 1000` before passing)
 * @param packetLoss - Packet loss as a percentage (0-100)
 */
export function calculateMOS(
  latency: number | undefined,
  jitter: number | undefined,
  packetLoss: number | undefined
): number {
  // Use conservative defaults when metrics are unavailable to avoid
  // inflating the score. These represent cautious mid-range assumptions
  // rather than ideal (0) values.
  const rtt = latency ?? 100;
  const jitterMs = jitter ?? 20;
  const lossPercent = packetLoss ?? 2;

  // R0 = 93.2 is the default narrowband E-model baseline
  // (R0 - Is = 93.2 with default noise/loudness conditions)
  const r0 = 93.2;

  // Estimate effective one-way delay: half the RTT plus jitter buffer delay
  const oneWayDelay = rtt / 2;
  const ta = oneWayDelay + 2 * jitterMs;

  // Delay impairment (Idd) — conversational interactivity loss
  const id = calculateIdd(ta);

  // Equipment/packet-loss impairment (Ie-eff)
  const ieEff = calculateIeEff(lossPercent);

  // R-factor: simplified E-model (A = 0 for landline/WiFi, no advantage factor)
  const r = r0 - id - ieEff;

  // Convert R-factor to MOS (ITU-T G.107, Annex B)
  return rFactorToMOS(r);
}

/**
 * Calculate the R-factor directly (exposed for reporting/debugging).
 */
export function calculateRFactor(
  latency: number | undefined,
  jitter: number | undefined,
  packetLoss: number | undefined
): number {
  const rtt = latency ?? 100;
  const jitterMs = jitter ?? 20;
  const lossPercent = packetLoss ?? 2;

  const r0 = 93.2;
  const ta = rtt / 2 + 2 * jitterMs;
  const id = calculateIdd(ta);
  const ieEff = calculateIeEff(lossPercent);

  return Math.max(0, Math.min(100, r0 - id - ieEff));
}

/**
 * Convert MOS score to quality category.
 *
 * Thresholds:
 *   🟢 Excellent: MOS >= 4.0  (R >= 80)
 *   🟡 Suboptimal: MOS >= 3.5  (R >= 70)
 *   🔴 Poor: MOS < 3.5  (R < 70)
 */
export function getMOSCategory(mos: number): QualityScore {
  if (mos >= 4.0) return QualityScore.Excellent;
  if (mos >= 3.5) return QualityScore.Good;
  if (mos >= 2.5) return QualityScore.Suboptimal;
  return QualityScore.Poor;
}

/**
 * Get descriptive text for MOS score.
 */
export function getMOSDescription(mos: number): string {
  if (mos >= 4.0) return 'Excellent';
  if (mos >= 3.5) return 'Good';
  if (mos >= 2.5) return 'Suboptimal';
  return 'Poor';
}

/**
 * Sanitize packet loss stats to remove startup/teardown artifacts.
 *
 * The Twilio SDK collects ~1 packet loss sample per second. On short tests
 * (10-15s), the first or last sample can record 100% loss during ICE
 * establishment or stream teardown. Because the SDK only exposes pre-computed
 * {min, max, average} (not raw samples), we estimate a corrected average by
 * removing outlier samples when a clear artifact pattern is detected:
 *
 *   Pattern: max ≈ 100% AND (average - min) is large AND min is reasonable
 *
 * Correction: assume 1 outlier sample at `max` value and back-calculate:
 *   corrected_avg ≈ (avg * n - max) / (n - 1)
 *
 * @param stats - Original packet loss stats from Twilio SDK (percentage 0-100)
 * @param testDurationMs - Test duration in milliseconds (default 15000)
 * @returns Sanitized stats with corrected average, or original stats if no artifact detected
 */
export function sanitizePacketLossStats(
  stats: { average: number; max: number; min: number },
  testDurationMs: number = 15000
): { average: number; max: number; min: number } {
  // Estimate number of samples: Twilio collects every 1s, first sample discarded
  const estimatedSamples = Math.max(2, Math.floor(testDurationMs / 1000) - 1);

  // Strategy 1: Detect clear 100% spike artifact
  // Pattern: max ≈ 100% AND min is low AND the spike is an outlier (not sustained)
  const hasOutlier = stats.max >= 90 && stats.min < 10 && (stats.max - stats.average) > 20;

  // Strategy 2: Detect moderate ICE establishment artifacts
  // During the first ~2 seconds of the preflight test, ICE is establishing and
  // no media flows. The SDK records this as high packet loss (often 30-100%) for
  // those samples. Even on a perfect connection, these 1-2 bad samples inflate
  // the average. Detect this pattern: min is very low (connection works fine once
  // established) but average is elevated beyond what min suggests.
  const iceArtifactSamples = 2;
  const hasIceArtifact = !hasOutlier
    && stats.min < 1
    && stats.average > 1
    && estimatedSamples > iceArtifactSamples + 2;

  if (hasOutlier) {
    // Remove the single worst outlier from the average
    const correctedAvg = Math.max(0, (stats.average * estimatedSamples - stats.max) / (estimatedSamples - 1));

    // Estimate a corrected max: the second-worst sample is likely much closer
    // to the corrected average than the original max.
    const correctedMax = Math.min(stats.max, Math.max(correctedAvg * 3, stats.min * 5, 5));

    return { average: correctedAvg, max: correctedMax, min: stats.min };
  }

  if (hasIceArtifact) {
    // Remove up to 2 worst samples (assumed ICE establishment artifacts).
    // Conservative: assume the worst samples were at the max value.
    const withoutWorst = Math.max(0,
      (stats.average * estimatedSamples - stats.max * iceArtifactSamples) /
      (estimatedSamples - iceArtifactSamples)
    );
    const correctedAvg = Math.max(stats.min, withoutWorst);
    const correctedMax = Math.min(stats.max, Math.max(correctedAvg * 3, 2));

    return { average: correctedAvg, max: correctedMax, min: stats.min };
  }

  return stats;
}

export function getQualityScore(
  preflightTestReport: PreflightTestReport | null,
  bitrateTestReport: MediaConnectionBitrateTest.Report | null
) {
  const maxBitrate = bitrateTestReport?.values ? Math.max(...bitrateTestReport.values) : 0;

  const latency = {
    average: formatNumber(preflightTestReport?.stats!.rtt!.average),
    max: formatNumber(preflightTestReport?.stats!.rtt!.max),
    qualityScore: getSingleQualityScore(preflightTestReport?.stats!.rtt!.average, 150, 300, 400),
  };

  // The Twilio SDK returns jitter in seconds (per WebRTC RFC 3550 / inbound-rtp stats).
  // Unlike RTT which the SDK converts to ms, jitter is passed through raw.
  // Convert to milliseconds for display and threshold comparison.
  const jitterAvgMs = preflightTestReport?.stats?.jitter?.average != null
    ? preflightTestReport.stats.jitter.average * 1000
    : undefined;
  const jitterMaxMs = preflightTestReport?.stats?.jitter?.max != null
    ? preflightTestReport.stats.jitter.max * 1000
    : undefined;

  const jitter = {
    average: formatNumber(jitterAvgMs),
    max: formatNumber(jitterMaxMs),
    qualityScore: getSingleQualityScore(jitterAvgMs, 30, 50, 100),
  };

  // Sanitize packet loss to remove startup/teardown artifacts from short tests.
  // Use the actual test duration from the report if available.
  const rawPacketLoss = preflightTestReport?.stats?.packetLoss;
  const testDurationMs = preflightTestReport?.testTiming?.duration ?? 15000;
  const sanitizedPacketLoss = rawPacketLoss
    ? sanitizePacketLossStats(rawPacketLoss, testDurationMs)
    : null;

  const packetLoss = {
    average: sanitizedPacketLoss ? formatNumber(sanitizedPacketLoss.average) : formatNumber(rawPacketLoss?.average),
    max: sanitizedPacketLoss ? formatNumber(sanitizedPacketLoss.max) : formatNumber(rawPacketLoss?.max),
    qualityScore: getSingleQualityScore(sanitizedPacketLoss?.average ?? rawPacketLoss?.average, 1, 3, 5),
  };

  const bitrate = {
    average: bitrateTestReport?.averageBitrate != null ? formatNumber(bitrateTestReport.averageBitrate) : undefined,
    max: bitrateTestReport?.values?.length ? formatNumber(maxBitrate) : undefined,
    qualityScore: getSingleQualityScore(bitrateTestReport?.averageBitrate!, 1000, 500, 150, true),
  };

  // Calculate MOS (Mean Opinion Score) using sanitized packet loss
  // and jitter converted to milliseconds
  const mosScore = calculateMOS(
    preflightTestReport?.stats?.rtt?.average,
    jitterAvgMs,
    sanitizedPacketLoss?.average ?? rawPacketLoss?.average
  );
  
  const mos = {
    score: mosScore,
    formattedScore: formatNumber(mosScore),
    category: getMOSCategory(mosScore),
    description: getMOSDescription(mosScore),
  };
  
  // Use MOS category as the primary quality indicator, but also consider bitrate.
  // Filter out null scores (missing data) so they don't artificially inflate the result.
  const scores: QualityScore[] = [
    mos.category,
    bitrate.qualityScore,
    latency.qualityScore,
    jitter.qualityScore,
    packetLoss.qualityScore,
  ].filter((s): s is QualityScore => s !== null);

  const totalQualityScore: QualityScore =
    scores.length > 0 ? (Math.min(...scores) as QualityScore) : QualityScore.Poor;

  return {
    latency,
    jitter,
    packetLoss,
    bitrate,
    mos,
    totalQualityScore,
  };
}
